/**
 * StreamingCustomerChat
 *
 * Customer-facing chat view for mission execution.
 * Renders streaming events as a clean chat conversation:
 * - Agent messages (tell_user, agent turns) as chat bubbles
 * - ask_user as interactive prompts
 * - Tool calls as collapsible inline cards
 * - Simplified progress steps (no node graph)
 * - Audience filtering: only shows 'external' messages
 * - Friendly error messages (no stack traces)
 *
 * Wraps useStreamingMissionChat hook.
 *
 * Design Decision — Audience Filtering:
 * Streaming WebSocket events from mission-runner are always customer-facing.
 * The `audience` field on ChatMessage is a chat-service concept for stored
 * messages (internal agent-to-agent vs external customer-visible). It does not
 * apply to streaming messages, which are inherently external. No audience
 * filtering code is needed here — all streamed events are shown to the user.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { Alert, AlertDescription, AlertTitle, Button, ChatInput, Input, Label, SystemMessage, TrustBadge, type TrustTier } from '@agion/ui'
import {
  CheckCircle2,
  ChevronDown,
  Loader2,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneOff,
  Play,
  Search,
  Target,
  Volume2,
  XCircle,
} from 'lucide-react'
import { buildCarriedTranscript, earlierRunsTranscript, settleLaunch, withoutRepeatedRows } from './followUpTranscript'
import { storedRunRows } from './storedRun'
import { ENDED_STATUSES, useLaunchedRunEnding } from './useLaunchedRunEnding'
import { useRunEndRecord, type RecordedRun } from './useRunEnd'
import { runCutNote, runEndCodeText, runEndLine as runEndLineText, runEndReading } from '../../utils/run-end'
import { acceptsMessagesWhileWorking, attachBlockedReason, composerText, pasteRefusalText, runNotReadyText, sendDisabledText } from './composerTexts'
import { asSentence } from './deliveryTexts'
import { launchRefusal, type LaunchRefusal } from './launchRefusalTexts'
import { config, operatorTakeoverEnabled } from '../../utils/config'
import { useStreamingMissionChat } from '../../hooks/useStreamingMissionChat'
import { useStreamingVoice } from '../../hooks/useStreamingVoice'
import { useMissionStore } from '../../hooks/useMissions'
import { useAuth } from '../../hooks/useAuth'
import { useEscalationStore } from '../../hooks/useEscalations'
import { OPERATOR_MODE_PARAM, runConversationHref } from '../../services/operator-takeover'
import { OperatorTakeoverPanel } from './OperatorTakeoverPanel'
import { useChatFileUpload } from '../../hooks/useChatFileUpload'
import { useComposerReturns } from '../../hooks/useComposerReturns'
import { useLaunchInputs } from '../../hooks/useLaunchInputs'
import { buildLaunchInput, humaniseName, initialLaunchValues, launchEntryOf } from '../../utils/launchInputs'
import { choiceQuestion } from '../../utils/questionOptions'
import { LaunchInputsForm } from './LaunchInputsForm'
import type { StreamingChatMessage } from '../../hooks/useStreamingMissionChat'
import { neutralEnding, neutralEndingMessage } from '../../utils/neutral-endings'
import { ANSWER_TIMEOUT, isAnswerTimeout } from '../../utils/answer-timeout'
import { toolReportedReason } from '../../utils/tool-reported-error'
import { shouldShowChatFade } from '../../utils/scrollFade'
import type { BYOHChatMessageResponse } from '../../services/api'
import type { HeldSubject } from '../../types/escalations'
import { missionsApi, executionsApi, byohProxyApi } from '../../services/api'
import { apiClient } from '../../services/api-client'
import { useMessageSearch } from '../../hooks/useMessageSearch'
import { SearchResults } from './SearchResults'
import { HITLContextBar } from './HITLContextBar'
import { CustomerMessageRenderer } from './CustomerMessageRenderer'
import { conversationMessages, fitsInMessage, outputFilesFromStoredToolOutput, unknownKind, type UserMessageAttachment } from '@agion/mission-stream'
import { useComposerPaste } from '../../hooks/useComposerPaste'
import { HistoryLoadNotice } from './HistoryLoadNotice'
import { resolveEdgeHost, useMissionPlacement, useRunEdgeHost } from '../../hooks/useEdgeHost'
import { CONVERSATION_NOT_VIEWABLE, EdgeHostUnknownError, isConversationNotViewable } from '../../services/api-failure'
import { ClosedConversationNotice } from './ClosedConversationNotice'
import { useChatSurface, type ActivityRunStatus, type ConversationRunStep, type HeaderActionItem, type HeaderProps } from './chatSurface'
import { placeRunSteps } from './runStepPlacement'
import { buildFeedbackRequest } from './feedbackRequest'
import { questionCardOf } from '../../utils/questionCard'
import type { GovernanceContext } from '../../types/streaming'
import { FileAttachment, type FileAttachmentFile } from '@agion/ui'

/** Execution statuses and mission outcomes that mean the user stopped the run. */
const STOPPED_OUTCOMES = ['cancelled', 'stopped', 'user_ended']
/** Hold states (Go missionstream OperatorHold*) the run reports when a hold ended. */
const ENDED_HOLD_STATES: ReadonlySet<string> = new Set(['returned', 'expired', 'unavailable'])

/** Without a surface that draws the run's steps the conversation has none. */
const NO_RUN_STEPS: ConversationRunStep[] = []
function useNoRunSteps(): ConversationRunStep[] {
  return NO_RUN_STEPS
}

/** Detect if a string is structured tool/system output rather than
 *  a natural-language assistant response. Returns parsed object if so. */
function isToolOutputJson(content: string): Record<string, unknown> | null {
  if (!content) return null
  const trimmed = content.trimStart()
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return null
  try {
    const parsed = JSON.parse(trimmed)
    if (Array.isArray(parsed)) return { results: parsed }
    const obj = parsed as Record<string, unknown>
    // Known tool output / error signatures
    if ('provider_response' in obj || 'provider_raw_response' in obj ||
        'hits' in obj || 'results' in obj || 'tool_output' in obj ||
        'error' in obj || 'status_code' in obj || 'http_status' in obj ||
        'query' in obj || 'documents' in obj || 'chunks' in obj) {
      return obj
    }
    return null
  } catch {
    return null
  }
}

/** Conversion result with extracted channel user identity map */
interface ConvertedHistory {
  messages: StreamingChatMessage[]
  /** Map: user_id UUID → external display name (phone number, email) from channel_user events */
  channelUserNames: Record<string, string>
}

/** Convert BYOH chat history rows into StreamingChatMessage format.
 *  Detects JSON tool outputs in assistant messages and converts them
 *  to tool_result entries instead of plain agent turns.
 *  Also extracts channel_user events to build user_id → display name map.
 *  A reply a person wrote in an operator takeover (author_kind "operator",
 *  agion/Agion#3435) becomes an operator_turn and never an agent turn; with
 *  takeover off it is not shown. */
export function convertHistoryToStreamingMessages(
  messages: BYOHChatMessageResponse[],
  missionId: string,
): ConvertedHistory {
  const result: StreamingChatMessage[] = []
  const channelUserNames: Record<string, string> = {}

  // Pre-scan: collect HITL prompts and responses from system events
  // so we can skip their plain assistant/user duplicates
  const hitlPrompts = new Set<string>()
  const hitlResponses = new Set<string>()
  for (const m of messages) {
    if (m.role !== 'system') continue
    try {
      const evt = JSON.parse(m.content) as Record<string, unknown>
      if (evt.event === 'hitl_request' && evt.prompt) hitlPrompts.add(evt.prompt as string)
      if (evt.event === 'hitl_response' && evt.response) hitlResponses.add(evt.response as string)
    } catch { /* skip */ }
  }

  const showOperatorTurns = operatorTakeoverEnabled()
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]!
    const ts = Date.parse(msg.created_at)

    if (msg.author_kind === 'operator') {
      if (!showOperatorTurns || !msg.content) continue
      result.push({
        id: `history-${msg.id}`,
        type: 'operator_turn',
        timestamp: ts,
        missionId,
        data: {
          holdId: msg.hold_id,
          messageId: msg.id,
          operatorUserId: msg.operator_user_id,
          operatorDisplayName: msg.operator_display_name,
          text: msg.content,
        },
      })
    } else if (msg.role === 'user') {
      // Skip user messages that are HITL response duplicates (structured system event handles these)
      if (hitlResponses.has(msg.content)) continue

      // Emit as hitl_response — this is what the chat canvas and audit trail both consume.
      // No separate agent_turn needed: hitl_response carries the user's message and identity.
      result.push({
        id: `history-${msg.id}`,
        type: 'hitl_response',
        timestamp: ts,
        missionId,
        data: {
          requestId: '',
          response: msg.content,
          modality: 'text',
          userId: msg.user_id ?? undefined,
          userName: msg.user_name ?? undefined,
          responseTimeMs: 0,
        },
      })

      // If next message is a tool output (not natural language), emit it separately
      const next = messages[i + 1]
      if (next && (next.role === 'agent' || next.role === 'assistant')) {
        const toolOutput = isToolOutputJson(next.content)
        if (toolOutput) {
          const hasError = 'error' in toolOutput
          const errorObj = toolOutput.error as Record<string, unknown> | string | undefined
          const provider = (toolOutput.provider_response as Record<string, unknown>)?.provider as string
            ?? (toolOutput.provider_raw_response as Record<string, unknown>)?.provider as string
            ?? (toolOutput.provider as string)
            ?? ''
          result.push({
            id: `history-${next.id}`,
            type: 'tool_result',
            timestamp: Date.parse(next.created_at),
            missionId,
            data: {
              toolCallId: '',
              toolName: provider,
              success: !hasError,
              output: toolOutput,
              error: hasError
                ? (typeof errorObj === 'string' ? errorObj : (errorObj as Record<string, unknown>)?.error_message as string ?? JSON.stringify(errorObj))
                : undefined,
              durationMs: next.latency_ms ?? 0,
            },
          })
          i++ // skip paired message
          continue
        }
        // Normal user→agent pair: the next iteration processes the agent row
        // as an assistant message, which emits it as hitl_request for the chat
        // canvas unless a hitl_request system event already carries it.
      }
    } else if (msg.role === 'agent' || msg.role === 'assistant') {
      // Skip assistant messages that are HITL prompt duplicates
      if (hitlPrompts.has(msg.content)) continue
      // Check for tool_calls JSONB (from ReportToolCall)
      if (msg.tool_calls && Array.isArray(msg.tool_calls) && msg.tool_calls.length > 0) {
        for (const tc of msg.tool_calls as Array<{ id?: string; function?: { name?: string; arguments?: string } }>) {
          let toolInput: Record<string, unknown> = {}
          try { toolInput = JSON.parse(tc.function?.arguments ?? '{}') } catch { /* ignore */ }
          result.push({
            id: `history-tc-${msg.id}-${tc.id ?? i}`,
            type: 'tool_call',
            timestamp: ts,
            missionId,
            agentId: msg.agent_id ?? undefined,
            data: {
              toolCallId: tc.id ?? '',
              toolName: tc.function?.name ?? 'unknown',
              toolInput,
            },
          })
        }
        continue // tool_call assistant messages have empty content
      }
      const toolOutput = isToolOutputJson(msg.content)
      if (toolOutput) {
        // This is a tool/system result stored as an assistant message
        const hasError = 'error' in toolOutput
        const errorObj = toolOutput.error as Record<string, unknown> | string | undefined
        const provider = (toolOutput.provider_response as Record<string, unknown>)?.provider as string
          ?? (toolOutput.provider_raw_response as Record<string, unknown>)?.provider as string
          ?? (toolOutput.provider as string)
          ?? ''
        result.push({
          id: `history-${msg.id}`,
          type: 'tool_result',
          timestamp: ts,
          missionId,
          data: {
            toolCallId: '',
            toolName: provider,
            success: !hasError,
            output: toolOutput,
            error: hasError
              ? (typeof errorObj === 'string' ? errorObj : (errorObj as Record<string, unknown>)?.error_message as string ?? JSON.stringify(errorObj))
              : undefined,
            durationMs: msg.latency_ms ?? 0,
          },
        })
      } else if (msg.content) {
        // Natural-language assistant message → emit as hitl_request for the chat canvas
        // (the chat only shows hitl_request/hitl_response types)
        result.push({
          id: `history-${msg.id}`,
          type: 'hitl_request',
          timestamp: ts,
          missionId,
          agentId: msg.agent_id ?? undefined,
          data: {
            requestId: '',
            prompt: msg.content,
            modality: 'text',
            agentLabel: msg.agent_name ?? undefined,
            pending: false,
          },
        })
      }
    } else if (msg.role === 'tool') {
      // Tool result row — persisted by ChatReporter.ReportToolResult
      let output: unknown = msg.content
      try { output = JSON.parse(msg.content) } catch { /* keep as string */ }
      const hasError = typeof output === 'object' && output !== null && 'error' in (output as Record<string, unknown>)
      // The files it made, as the live mission.tool.result carries them (agion/Agion#2003).
      const outputFiles = outputFilesFromStoredToolOutput(msg.content, msg.id)
      result.push({
        id: `history-${msg.id}`,
        type: 'tool_result',
        timestamp: ts,
        missionId,
        data: {
          toolCallId: msg.tool_call_id ?? '',
          toolName: '',
          success: !hasError,
          output,
          error: hasError ? ((output as Record<string, unknown>).error as Record<string, unknown>)?.error_message as string ?? '' : undefined,
          durationMs: msg.latency_ms ?? 0,
          ...(outputFiles.length > 0 ? { outputFiles } : {}),
        },
      })
    } else if (msg.role === 'system') {
      // System event row — persisted by ChatReporter for node_status, orchestrator, delegation
      try {
        const evt = JSON.parse(msg.content) as Record<string, unknown>
        const eventType = evt.event as string
        switch (eventType) {
          case 'node_status':
            result.push({
              id: `history-${msg.id}`,
              type: 'node_status',
              timestamp: ts,
              missionId,
              nodeId: evt.node_id as string,
              data: { status: evt.status as string },
            })
            break
          case 'orchestrator_decision':
            result.push({
              id: `history-${msg.id}`,
              type: 'orchestrator_decision',
              timestamp: ts,
              missionId,
              data: {
                iteration: evt.iteration as number,
                action: evt.action as string,
                reasoning: evt.reasoning as string,
                targetAgent: evt.target_agent as string,
              },
            })
            break
          case 'delegation':
            result.push({
              id: `history-${msg.id}`,
              type: 'delegation',
              timestamp: ts,
              missionId,
              data: {
                agentLabel: evt.agent_label as string,
                task: evt.task as string,
                depth: evt.depth as number,
              },
            })
            break
          case 'delegation_result':
            result.push({
              id: `history-${msg.id}`,
              type: 'delegation_result',
              timestamp: ts,
              missionId,
              data: {
                agentLabel: evt.agent_label as string,
                success: evt.success as boolean,
                summary: evt.summary as string,
                error: evt.error as string,
                durationMs: msg.latency_ms ?? 0,
                outcome: evt.outcome as string | undefined,
                blockedReason: evt.blocked_reason as string | undefined,
              },
            })
            break
          case 'hitl_request': {
            // A question card frame (agion/Agion#3446) reloads as the card
            // only: never its options, never an open question.
            const questionCard = questionCardOf(evt.governance_context as GovernanceContext | undefined)
            if (questionCard) {
              result.push({
                id: `history-${msg.id}`,
                type: 'hitl_request',
                timestamp: ts,
                missionId,
                agentId: (evt.agent_id as string) || msg.agent_id || undefined,
                nodeId: (evt.node_id as string) || undefined,
                data: {
                  requestId: evt.request_id as string,
                  modality: 'message',
                  prompt: evt.prompt as string,
                  questionCard,
                  pending: false,
                },
              })
              break
            }
            result.push({
              id: `history-${msg.id}`,
              type: 'hitl_request',
              timestamp: ts,
              missionId,
              agentId: (evt.agent_id as string) || msg.agent_id || undefined,
              nodeId: (evt.node_id as string) || undefined,
              data: {
                requestId: evt.request_id as string,
                modality: evt.modality as string ?? 'text',
                prompt: evt.prompt as string,
                options: evt.options as string[] | undefined,
                ...(evt.option_details ? { optionDetails: evt.option_details } : {}),
                ...(choiceQuestion(evt.question) ? { question: choiceQuestion(evt.question) } : {}),
                pending: false,
              },
            })
            break
          }
          case 'hitl_response':
            result.push({
              id: `history-${msg.id}`,
              type: 'hitl_response',
              timestamp: ts,
              missionId,
              data: {
                requestId: evt.request_id as string,
                response: evt.response as string,
                responseTimeMs: evt.response_time_ms as number ?? 0,
                userId: evt.user_id as string,
                userName: evt.user_name as string,
              },
            })
            break
          case 'agent_thinking':
            result.push({
              id: `history-${msg.id}`,
              type: 'agent_thinking',
              timestamp: ts,
              missionId,
              agentId: msg.agent_id ?? undefined,
              data: {
                turnNumber: 0,
                content: evt.content as string ?? '',
                redacted: evt.redacted as boolean ?? false,
                model: evt.model as string,
              },
            })
            break
          case 'mission_ended':
            if (!(evt.success as boolean) && !evt.stopped && evt.error) {
              result.push({
                id: `history-${msg.id}`,
                type: 'mission_failed',
                timestamp: ts,
                missionId,
                data: { error: evt.error as string, outcome: evt.outcome as string | undefined },
              })
            } else {
              result.push({
                id: `history-${msg.id}`,
                type: 'mission_completed',
                timestamp: ts,
                missionId,
                data: { outcome: evt.outcome as string ?? (evt.stopped ? 'stopped' : 'success') },
              })
            }
            break
          case 'governance_decision':
            result.push({
              id: `history-${msg.id}`,
              type: 'governance_event',
              timestamp: ts,
              missionId,
              data: {
                action: evt.action as string,
                ruleName: evt.rule_name as string,
                reason: evt.reason as string,
                trustTier: evt.trust_tier as string,
              },
            })
            break
          case 'channel_user': {
            // Maps conversation user_id → external display name (phone, email)
            const externalId = evt.external_user_id as string
            const channelMode = evt.channel_mode as string
            if (externalId && msg.user_id) {
              const displayName = channelMode === 'whatsapp'
                ? `${externalId} (WhatsApp)`
                : channelMode === 'sms'
                ? `${externalId} (SMS)`
                : externalId
              channelUserNames[msg.user_id] = displayName
            }
            break
          }
        }
      } catch {
        // Unparseable system message — skip
      }
    }
  }
  return { messages: result, channelUserNames }
}

/**
 * The earlier runs of a held conversation read for one hold
 * (agion/Agion#3435): the runs in the hold read's order, the stored turns of
 * each run read so far, and the runs whose read failed.
 */
interface HeldEarlierRuns {
  holdId: string | null
  order: string[]
  loaded: Record<string, StreamingChatMessage[]>
  failed: string[]
}

const NO_EARLIER_RUNS: HeldEarlierRuns = { holdId: null, order: [], loaded: {}, failed: [] }

/**
 * Reads one run's stored conversation from the Edge host that holds it, as
 * the chat shows it: a run reopened from the address, and a run this chat
 * launched once it has ended (agion/Agion#2871).
 */
async function readRunHistory(hostId: string, missionId: string, executionId: string): Promise<ConvertedHistory> {
  let sessionId: string | undefined
  try {
    sessionId = await executionToSessionId(executionId)
  } catch {
    console.warn('[StreamingCustomerChat] Failed to compute session_id for execution', executionId)
  }
  const response = await byohProxyApi.getMissionMessagesByHost(hostId, missionId, 500, undefined, sessionId, executionId)
  return convertHistoryToStreamingMessages(response.messages || [], missionId)
}

/**
 * Compute the deterministic session UUID from an execution ID.
 * Mirrors mission-runner/pkg/chat/reporter.go: uuid.NewSHA1(NameSpaceDNS, executionID)
 */
async function executionToSessionId(executionId: string): Promise<string> {
  // DNS namespace UUID bytes: 6ba7b810-9dad-11d1-80b4-00c04fd430c8
  const DNS_NS = new Uint8Array([
    0x6b, 0xa7, 0xb8, 0x10, 0x9d, 0xad, 0x11, 0xd1,
    0x80, 0xb4, 0x00, 0xc0, 0x4f, 0xd4, 0x30, 0xc8,
  ])
  const nameBytes = new TextEncoder().encode(executionId)
  const data = new Uint8Array(DNS_NS.length + nameBytes.length)
  data.set(DNS_NS)
  data.set(nameBytes, DNS_NS.length)

  const hash = new Uint8Array(await crypto.subtle.digest('SHA-1', data))
  hash[6] = (hash[6]! & 0x0f) | 0x50 // version 5
  hash[8] = (hash[8]! & 0x3f) | 0x80 // variant RFC 4122
  const h = Array.from(hash.slice(0, 16), (b) => b.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`
}

function formatVoiceDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// ============================================================================
// VoiceCallStrip — compact bar above the composer during a voice call
// ============================================================================

function VoiceCallStrip({ state, isRemoteSpeaking, isLocalSpeaking, durationSeconds, isMuted, onToggleMute, onDisconnect }: {
  state: 'connecting' | 'in_call'
  isRemoteSpeaking: boolean
  isLocalSpeaking: boolean
  durationSeconds: number
  isMuted: boolean
  onToggleMute: () => void
  onDisconnect: () => void
}) {
  const { t } = useTranslation(['chat'])
  return (
    <div className="max-w-3xl mx-auto mb-2">
      <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
        {state === 'connecting' ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs text-emerald-700 dark:text-emerald-300 flex-1">
              {t('chat:voice.connecting', 'Connecting to voice...')}
            </span>
          </>
        ) : (
          <>
            {isRemoteSpeaking ? (
              <Volume2 className="w-3.5 h-3.5 text-violet-500 animate-pulse" />
            ) : isLocalSpeaking ? (
              <Mic className="w-3.5 h-3.5 text-blue-500 animate-pulse" />
            ) : (
              <Phone className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            )}
            <span className="text-xs text-emerald-700 dark:text-emerald-300 flex-1">
              {isRemoteSpeaking
                ? t('chat:voice.agentSpeaking', 'Agent is speaking...')
                : isLocalSpeaking
                ? t('chat:voice.listening', 'Listening...')
                : t('chat:voice.inCall', 'Voice call active')}
            </span>
            <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400 tabular-nums">
              {formatVoiceDuration(durationSeconds)}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleMute}
              className={`p-1 h-auto w-auto rounded-full transition-colors ${
                isMuted
                  ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-500'
                  : 'bg-emerald-100 dark:bg-emerald-800/50 text-emerald-600 dark:text-emerald-400'
              }`}
              title={isMuted ? t('chat:voice.unmute', 'Unmute') : t('chat:voice.mute', 'Mute')}
              aria-label={isMuted ? t('chat:voice.unmute', 'Unmute') : t('chat:voice.mute', 'Mute')}
            >
              {isMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDisconnect}
              className="p-1 h-auto w-auto rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-500 hover:bg-rose-200 dark:hover:bg-rose-900/50 transition-colors"
              title={t('chat:voice.endCall', 'End call')}
              aria-label={t('chat:voice.endCall', 'End call')}
            >
              <PhoneOff className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// MissionHeader — expandable header with mission context
// ============================================================================

// `search` (agion/Agion#2612 item 2) goes unused here: the current chat keeps
// its own standalone full-width search row (StreamingCustomerChat.tsx), only
// the next chat surface's Header folds search into itself.
function MissionHeader({
  missionName, executionCreatedAt, trustTier, hostName,
  isConnected, isHistoryOnly, executionId, pastExecutions, onNavigateExecution,
}: HeaderProps) {
  const { t } = useTranslation(['chat'])
  const [expanded, setExpanded] = React.useState(false)

  // Filter past executions to exclude the current one
  const otherExecutions = React.useMemo(
    () => (pastExecutions || []).filter(e => e.id !== executionId).slice(0, 6),
    [pastExecutions, executionId],
  )

  return (
    <div className="border-b border-slate-200 dark:border-slate-700">
      {/* Compact header row */}
      <div
        className="flex items-center gap-2.5 px-4 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
      >
        <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <Target className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
        </div>
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white truncate flex-1">
          {missionName}
        </h2>
        {executionCreatedAt && (
          <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">
            {new Date(executionCreatedAt).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </span>
        )}
        {trustTier != null && (
          <TrustBadge tier={trustTier} size="sm" />
        )}
        {executionId && !isConnected && !isHistoryOnly && (
          <span className="text-xs text-amber-500 flex-shrink-0">
            {t('chat:customer.reconnecting', 'Reconnecting...')}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`} />
      </div>

      {/* Expandable detail section */}
      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-2 text-xs">
          {/* Host */}
          {hostName && (
            <div className="flex items-center justify-between">
              <span className="text-slate-400 dark:text-slate-500">{t('chat:header.host', 'Host')}</span>
              <span className="text-slate-700 dark:text-slate-300 font-medium">{hostName}</span>
            </div>
          )}

          {/* Run count */}
          {pastExecutions && pastExecutions.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-slate-400 dark:text-slate-500">{t('chat:header.runs', 'Runs')}</span>
              <span className="text-slate-700 dark:text-slate-300">{pastExecutions.length}</span>
            </div>
          )}

          {/* Previous runs */}
          {otherExecutions.length > 0 && (
            <div>
              <span className="text-slate-400 dark:text-slate-500 block mb-1.5">{t('chat:header.previousRuns', 'Previous runs')}</span>
              <div className="space-y-0.5">
                {otherExecutions.map((exec) => (
                  <Button variant="ghost" size="sm"
                    key={exec.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      onNavigateExecution(exec.id)
                      setExpanded(false)
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1 rounded text-left hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${exec.isActive ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                    <span className="text-slate-600 dark:text-slate-400">
                      {exec.timestamp.toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================

interface StreamingCustomerChatProps {
  missionId: string
  autostart?: boolean
  executionId?: string | null
  /** Called whenever the messages array changes — used by MissionChatWithArtifacts for artifact extraction */
  onMessagesChange?: (messages: StreamingChatMessage[]) => void
  /** Optional actions rendered on the right side of the chat header — used by MissionChatWithArtifacts to inject the reopen button */
  headerActions?: React.ReactNode
  /** The same actions, structured for the next chat surface's header row (agion/Agion issue 2612 item 5); the current chat ignores this and keeps rendering headerActions in its own row. */
  headerActionItems?: HeaderActionItem[]
  /** Trust tier for the mission's primary agent — shown as a TrustBadge in the header */
  trustTier?: TrustTier | null
  /** Host name for the expandable header */
  hostName?: string
  /** Past executions for the expandable header */
  pastExecutions?: Array<{ id: string; timestamp: Date; isActive?: boolean }>
  /** Called when the run's state changes, for an Activity sidebar that states it (agion/Agion#2827). */
  onRunStatusChange?: (status: ActivityRunStatus | undefined) => void
  /** Called when the signed-in member starts or stops holding this run's conversation in operator mode (agion/Agion#3435). */
  onOperatorHoldChange?: (held: boolean) => void
  /** Called with the held run's end user while operator mode names them, and undefined otherwise (agion/Agion#3435). */
  onHeldSubjectChange?: (subject: HeldSubject | undefined) => void
}

export const StreamingCustomerChat: React.FC<StreamingCustomerChatProps> = ({
  missionId,
  autostart = false,
  executionId: executionIdProp = null,
  onMessagesChange,
  headerActions,
  headerActionItems,
  trustTier,
  hostName,
  pastExecutions,
  onRunStatusChange,
  onOperatorHoldChange,
  onHeldSubjectChange,
}) => {
  // PLAN-20260929 slice 7: the composer and the end-of-run lines come from the
  // chat surface when it provides them; otherwise the current chat's own.
  const surface = useChatSurface()
  const Composer = surface.Composer ?? ChatInput
  const RunStatusLine = surface.RunStatusLine
  // The current chat says so too (agion/Agion#2481); the new chat supplies its own piece.
  const HistoryFailure = surface.HistoryFailure ?? HistoryLoadNotice
  const SystemNote = surface.SystemMessage ?? SystemMessage
  // PLAN-20260929 slice 12 batch 4: the mission header, the HITL context bar,
  // the start-values form and the search results dropdown come from the chat
  // surface when it provides them, otherwise the current chat's own.
  const Header = surface.Header ?? MissionHeader
  // Whether the active Header draws its own search affordance (agion/Agion#2612
  // item 2): only the next chat surface's Header reads the `search` prop this
  // component feeds it below; the current chat's MissionHeader ignores it and
  // keeps the standalone search row instead.
  const usesNextHeader = surface.Header != null
  const ContextBar = surface.ContextBar ?? HITLContextBar
  const LaunchInputs = surface.LaunchInputs ?? LaunchInputsForm
  const SearchResultsView = surface.SearchResults ?? SearchResults
  const VoiceControls = surface.VoiceControls ?? VoiceCallStrip
  const { t, i18n } = useTranslation(['chat', 'common'])
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const contextParam = searchParams.get('context')
  const [inputValue, setInputValue] = useState('')
  const [isLaunching, setIsLaunching] = useState(false)
  const [isRunning, setIsRunning] = useState(!!executionIdProp)
  const [isDragOver, setIsDragOver] = useState(false)
  // A send pressed while staged files still upload waits for them (phase 4).
  const [sendHeld, setSendHeld] = useState(false)
  // Why the last launch without a message failed; a launch with one says it
  // on its message. A refusal the chat knows by code carries its own words
  // and, for a person who can fix it, where (agion/Agion#2622).
  const [launchFailure, setLaunchFailure] = useState<LaunchRefusal | null>(null)
  const [executionId, setExecutionId] = useState<string | null>(executionIdProp)
  const [executionStatus, setExecutionStatus] = useState<string | null>(null)
  const [executionCreatedAt, setExecutionCreatedAt] = useState<string | null>(null)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  // A failed read of a past run, for a surface that says so (ADR-20260925).
  const [historyFailure, setHistoryFailure] = useState<unknown>(null)
  // The server does not open this run's content to the reader
  // (agion/Agion#3019): the run detail answered content_open false, or the
  // transcript read was refused. It carries the run's initiator_type.
  const [closedRun, setClosedRun] = useState<{ initiatorType?: string } | null>(null)
  const [historyAttempt, setHistoryAttempt] = useState(0)
  const reloadRun = useCallback(() => setHistoryAttempt((n) => n + 1), [])
  // Earlier runs' conversation plus the launch message, kept on screen across a
  // relaunch. The streaming hook resets its own list per execution. Agion#2064.
  const [carriedMessages, setCarriedMessages] = useState<StreamingChatMessage[]>([])
  const [resolvedChannelUsers, setResolvedChannelUsers] = useState<Record<string, string>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isLaunchingRef = useRef(false)
  // The run this chat launched, until the address names another one: the
  // address naming it loads no history over the conversation on screen.
  const launchedExecutionRef = useRef<string | null>(null)
  // The same run, for the reader of its stored record once it ends (agion/Agion#2871).
  const [launchedRun, setLaunchedRun] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  // The scroll region the bottom fade decorates (agion/Agion#2612 item 4) —
  // its own data-scroll-fade attribute, not the fade's always-on default,
  // gates next-chat-layout.css's ::after.
  const messagesScrollRef = useRef<HTMLDivElement>(null)
  const [hasScrollFade, setHasScrollFade] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Message search across BYOH hosts
  const messageSearch = useMessageSearch()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const mission = useMissionStore((s) => s.getMissionById(missionId))
  // The server's answer that a new run cannot start now, in reader words
  // (run_readiness, persona F-CHAT-08); undefined when it can or has not said,
  // and always on a surface that does not opt in (the current chat).
  const runNotReady = surface.runReadiness ? runNotReadyText(t, mission?.runReadiness) : undefined
  const runNotReadyRef = useRef(runNotReady)
  runNotReadyRef.current = runNotReady
  // The start values a new run asks for (agion/Agion#2413).

  // The Edge host that holds this conversation's files and history: the open
  // run's recorded host, else the mission's own; never a group id
  // (agion/Agion#2485).
  // A past run's host comes with its history load; a run launched here is read
  // on its own until dispatch has recorded it.
  const [pastRunHostId, setPastRunHostId] = useState<string | undefined>(undefined)
  // The run record read when a run is opened, which holds its end record.
  const [recordedRun, setRecordedRun] = useState<RecordedRun | null>(null)
  const launchedRunHostId = useRunEdgeHost(executionId && executionId !== executionIdProp ? executionId : null, isRunning)
  const runEdgeHostId = executionId && executionId !== executionIdProp ? launchedRunHostId : pastRunHostId
  // Before a group-bound mission has a run, the placement names the member a
  // new run goes to, so files go there first and the launch is pinned to it.
  const knownHostId = resolveEdgeHost({ runHostId: runEdgeHostId, missionHostId: mission?.hostId })
  const placement = useMissionPlacement(missionId, !!mission && !knownHostId, contextParam)
  const edgeHostId = knownHostId ?? placement.hostId
  // A follow-up without files may start on another member when the host of
  // the run it continues is not available; chat says so (question 2).
  // The host of the run a new run follows: the ?context= link's run, read
  // once, else the run this conversation showed when the new run started,
  // whose host is already known then.
  const contextRunHostId = useRunEdgeHost(contextParam, false)
  const [followedRunHostId, setFollowedRunHostId] = useState<string | undefined>(undefined)
  const previousRunHostId = contextParam ? contextRunHostId : followedRunHostId
  const runHostMoved = !!previousRunHostId && !!launchedRunHostId && launchedRunHostId !== previousRunHostId

  const {
    pendingFiles,
    isUploading,
    addFiles,
    removeFile,
    retryFile,
    getAttachments,
    reset: resetFiles,
    restore: restoreFiles,
  } = useChatFileUpload({ hostId: edgeHostId })

  const launchForm = useLaunchInputs({ userId: user?.id, missionId, inputs: mission?.launchInputs, canStart: mission?.canStart, files: pendingFiles, addFiles, retryFile, removeFile })
  const { take: takeStartValues, remember: rememberStartValues, refused: refusedForStartValues, clearFileValues } = launchForm
  // How a new run is entered (agion/Agion#3597): the message field, the one
  // text start value as the bottom field, or the start values form.
  const launchEntry = useMemo(
    () => launchEntryOf(mission),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the entry reads only these fields
    [mission?.executionMode, mission?.launchInputs, mission?.launchDeclaresMessage, mission?.hasVoiceCapability],
  )
  const startValueLabelId = `start-value-label-${useId()}`

  // Pasted cells become a Markdown table and pasted files are staged; a paste
  // that cannot be taken says why (phase 4, agion/Agion#2327).
  const { onPaste, pasteRefusal } = useComposerPaste({
    value: inputValue,
    setValue: setInputValue,
    canAttach: !!edgeHostId,
    addFiles,
  })

  // Skip WebSocket for completed/failed executions — no live events to receive
  const isHistoryOnly = !!executionStatus && ENDED_STATUSES.includes(executionStatus)
  // An adaptive or autonomous run takes a message while the agent works (phase 6).
  const acceptsMessages = acceptsMessagesWhileWorking(mission?.executionMode)

  const {
    messages,
    activeHITL,
    pendingHITLs,
    selectedHITLIndex,
    selectedHITL,
    selectHITL,
    isConnected,
    isCompleted: isCompletedLive,
    isFailed: isFailedLive,
    error,
    errorCode,
    voiceRoom,
    sendMessage,
    respondToHITL,
    returnedAnswer,
    notifyVoiceUserJoined,
    stopMission,
    isPaused,
    pauseMission,
    resumeMission,
    setInitialMessages,
    sendOperatorMessage,
    operatorReplies,
    operatorHoldStates,
  } = useStreamingMissionChat({
    missionId,
    executionId,
    enabled: true,
    skipSubscription: isHistoryOnly,
    acceptsMessages,
    // The server restored the run (P-095 Amendment S04-7): read the run and
    // its messages again. A run launched here keeps the conversation on
    // screen, as the history read below leaves it (agion/Agion#2327).
    onConnectionRestored: reloadRun,
  })

  // Operator mode (agion/Agion#3435): the address names a hold, and the
  // person who took over this run's conversation replies as themselves for it
  // and hands it back. The address alone opens nothing: operator mode opens
  // only once governance reads the hold as approved, a chat_handoff for this
  // run, with the signed-in member as its operator, and closes when the run
  // reports it ended or a later read no longer says so. Until then, and for
  // anyone else, the ordinary conversation stays.
  const requestedHoldId = operatorTakeoverEnabled() ? searchParams.get(OPERATOR_MODE_PARAM) : null
  const viewerId = user?.id
  const operatorHold = useEscalationStore((s) => (requestedHoldId ? s.operatorHolds.find((h) => h.id === requestedHoldId) : undefined))
  const refreshOperatorHold = useEscalationStore((s) => s.refreshOperatorHold)
  const returnHandoff = useEscalationStore((s) => s.returnHandoff)
  // The hold whose read has answered, so "not held by you" never shows while it loads.
  const [holdReadFor, setHoldReadFor] = useState<string | null>(null)
  const runHoldState = requestedHoldId ? operatorHoldStates[requestedHoldId] : undefined
  useEffect(() => {
    if (!requestedHoldId || !executionId || !viewerId) return
    let current = true
    void refreshOperatorHold(requestedHoldId, viewerId).then(() => {
      if (current) setHoldReadFor(requestedHoldId)
    })
    return () => { current = false }
  }, [requestedHoldId, executionId, viewerId, runHoldState, refreshOperatorHold])
  const heldByViewer = !!operatorHold && !!viewerId
    && operatorHold.status === 'approved'
    && operatorHold.escalation_type === 'chat_handoff'
    && operatorHold.execution_id === executionId
    && operatorHold.operator_id === viewerId
    && !(runHoldState !== undefined && ENDED_HOLD_STATES.has(runHoldState))
  const operatorHoldId = heldByViewer ? requestedHoldId : null
  const operatorMode = !!operatorHoldId
  // The held conversation's earlier runs (challenge
  // 2026-10-25-operator-takeover-earlier-runs C3): governance lists them in
  // the hold read for its operator only, oldest first and the held run last.
  // In operator mode each is read through the same transcript read as the
  // held run, from the held run's host, which is the only host the hold
  // opens, and shown before it. Each read is a grant with its own audit
  // entry, so each earlier run is read once per hold; a read of the hold that
  // lists no runs keeps what was shown.
  const earlierRunIds = operatorMode
    ? (operatorHold?.conversation_execution_ids ?? []).filter((id) => id !== executionIdProp).join(',')
    : ''
  const heldRunHostId = recordedRun?.executionId === executionIdProp
    ? resolveEdgeHost({ runHostId: pastRunHostId, missionHostId: mission?.hostId })
    : undefined
  const [earlierRuns, setEarlierRuns] = useState<HeldEarlierRuns>(NO_EARLIER_RUNS)
  const requestedEarlierRef = useRef<{ holdId: string | null; runIds: Set<string> }>({ holdId: null, runIds: new Set() })
  useEffect(() => {
    if (!operatorHoldId || !earlierRunIds || !heldRunHostId) return
    const holdId = operatorHoldId
    const runIds = earlierRunIds.split(',')
    if (requestedEarlierRef.current.holdId !== holdId) requestedEarlierRef.current = { holdId, runIds: new Set() }
    const requested = requestedEarlierRef.current.runIds
    setEarlierRuns((runs) => (runs.holdId === holdId ? { ...runs, order: runIds } : { holdId, order: runIds, loaded: {}, failed: [] }))
    for (const runId of runIds) {
      if (requested.has(runId)) continue
      requested.add(runId)
      readRunHistory(heldRunHostId, missionId, runId).then(
        ({ messages: stored }) => setEarlierRuns((runs) => (runs.holdId === holdId ? { ...runs, loaded: { ...runs.loaded, [runId]: stored } } : runs)),
        (err) => {
          console.warn('[StreamingCustomerChat] Failed to read an earlier run of the held conversation:', err)
          setEarlierRuns((runs) => (runs.holdId === holdId ? { ...runs, failed: [...runs.failed, runId] } : runs))
        },
      )
    }
  }, [operatorHoldId, earlierRunIds, heldRunHostId, missionId])
  const earlierRunDivider = t('chat:customer.newRunDivider', 'New run started. Earlier messages are shown for reference.')
  const shownEarlierRuns = operatorMode && earlierRuns.holdId === operatorHoldId ? earlierRuns : NO_EARLIER_RUNS
  const heldEarlierMessages = useMemo(
    () => earlierRunsTranscript(
      shownEarlierRuns.order.filter((runId) => runId in shownEarlierRuns.loaded)
        .map((runId) => ({ executionId: runId, messages: shownEarlierRuns.loaded[runId]! })),
      missionId,
      earlierRunDivider,
    ),
    [shownEarlierRuns, missionId, earlierRunDivider],
  )
  const earlierRunsIncomplete = shownEarlierRuns.failed.length > 0
  // The Activity panel beside the conversation reads the run as waiting on a
  // person while the viewer holds it: the operator subscribes after the
  // 'joined' notice went out, and history stores no hold notices.
  useEffect(() => {
    onOperatorHoldChange?.(heldByViewer)
  }, [heldByViewer, onOperatorHoldChange])
  const holdNotViewers = !!requestedHoldId && !!executionId && holdReadFor === requestedHoldId && !heldByViewer
  // A hold that was this person's and ended leaves operator mode.
  const wasHeldRef = useRef(false)
  useEffect(() => {
    if (heldByViewer) {
      wasHeldRef.current = true
      return
    }
    if (wasHeldRef.current && executionId) {
      wasHeldRef.current = false
      navigate(runConversationHref(missionId, executionId), { replace: true })
    }
  }, [heldByViewer, executionId, missionId, navigate])
  // The end user's turns name them as the hold read does, never by the
  // email a stored or live row may carry.
  const heldSubject = useMemo<HeldSubject | undefined>(
    () => (operatorMode && operatorHold?.subject_user_id
      ? { userId: operatorHold.subject_user_id, displayName: operatorHold.subject_display_name }
      : undefined),
    [operatorMode, operatorHold?.subject_user_id, operatorHold?.subject_display_name],
  )
  // The Activity panel names them the same way.
  useEffect(() => {
    onHeldSubjectChange?.(heldSubject)
  }, [heldSubject, onHeldSubjectChange])
  const holdReplies = useMemo(
    () => (operatorMode ? operatorReplies.filter((reply) => reply.holdId === operatorHoldId) : []),
    [operatorMode, operatorReplies, operatorHoldId],
  )
  const handBack = useCallback(async (): Promise<boolean> => {
    if (!operatorHoldId || !executionId) return false
    const returned = await returnHandoff(operatorHoldId)
    if (returned) navigate(runConversationHref(missionId, executionId), { replace: true })
    return returned
  }, [operatorHoldId, executionId, returnHandoff, navigate, missionId])

  // An answer typed here that failed or was refused comes back with its text
  // and files, and its question is open again (phase 4, agion/Agion#2327); a
  // message sent while the agent works comes back the same way (phase 6).
  // Every send that comes back is restored, in send order, before a draft
  // typed since. Each return applies once. What the message box then holds
  // survives a reload of this conversation until the box is empty again.
  const stagedAttachments = useMemo(() => getAttachments(), [getAttachments])
  useComposerReturns({
    returnedAnswer, setInputValue, restoreFiles,
    composer: { userId: user?.id, missionId, text: inputValue, attachments: stagedAttachments },
  })

  // Voice call — wire useStreamingVoice to voiceRoom from WebSocket
  const {
    voiceState,
    isMuted,
    isRemoteSpeaking,
    isLocalSpeaking,
    error: voiceError,
    duration: voiceDuration,
    connect: voiceConnect,
    disconnect: voiceDisconnect,
    toggleMute,
  } = useStreamingVoice({
    voiceRoom,
    onUserJoined: notifyVoiceUserJoined,
  })

  // Auto-connect to voice room when credentials arrive
  useEffect(() => {
    if (voiceRoom && voiceState === 'idle') {
      voiceConnect()
    }
  }, [voiceRoom, voiceState, voiceConnect])

  // Merge live and history-based completion status.
  // A run whose recorded status is "failed" must read as failed when it is reopened
  // from the run list, where there is no live error to carry the failure. Agion#2075.
  const isCompleted = isCompletedLive || executionStatus === 'completed' || executionStatus === 'cancelled' || executionStatus === 'failed'
  const isFailed = (isFailedLive && !!error) || executionStatus === 'failed'

  // A run the user stopped ends without converging, so it must not claim success.
  // The outcome arrives either as the execution row status (history) or on the
  // mission_completed event (live, and rebuilt from the mission_ended history event).
  const isStopped = useMemo(() => {
    if (executionStatus && STOPPED_OUTCOMES.includes(executionStatus)) return true
    const ended = [...carriedMessages, ...messages].filter((msg) => msg.type === 'mission_completed')
    const last = ended[ended.length - 1]
    if (!last) return false
    const outcome = (last.data as { outcome?: string }).outcome
    return !!outcome && STOPPED_OUTCOMES.includes(outcome)
  }, [executionStatus, carriedMessages, messages])

  // A run that delivered an answer to check ends as failed with a neutral outcome
  // (utils/neutral-endings.ts), live on mission.failed and rebuilt from the
  // mission_ended history event. It gets a neutral notice, not the crash line.
  // Only this run's messages count; carried ones belong to an earlier run.
  const neutralOutcome = useMemo(() => {
    const failures = messages.filter((msg) => msg.type === 'mission_failed')
    const last = failures[failures.length - 1]
    return neutralEnding((last?.data as { outcome?: string } | undefined)?.outcome)
  }, [messages])

  // The failure line names a run that ended because no answer came in time
  // (agion/Agion#2078), live and rebuilt from the mission_ended history event;
  // every other failure keeps the generic line. This run's failure event is
  // read first, then the live error it set.
  const failureText = useMemo(() => {
    const failures = messages.filter((msg) => msg.type === 'mission_failed')
    const last = failures[failures.length - 1]
    const reason = (last?.data as { error?: string } | undefined)?.error ?? (isFailedLive ? error : null)
    return isAnswerTimeout(reason)
      ? t(ANSWER_TIMEOUT.key, ANSWER_TIMEOUT.fallback)
      : t('chat:customer.missionFailed', 'Something went wrong. Please try again or contact support.')
  }, [messages, isFailedLive, error, t])

  // A run a generated tool stopped with its own reason (utils/tool-reported-error.ts)
  // shows that reason as a warning, live and rebuilt from history alike.
  const toolReportedError = useMemo(() => {
    const failures = messages.filter((msg) => msg.type === 'mission_failed')
    const data = failures[failures.length - 1]?.data as { outcome?: string; error?: string } | undefined
    return toolReportedReason(data?.outcome, data?.error)
  }, [messages])

  // The run is over however it ended. A live failure leaves isCompleted false, so
  // without this a question kept inviting an answer nobody could give. Agion#2076.
  const runEnded = isCompleted || isFailed
  // Why the run ended and what it lacked, from the server's end record
  // (P-095 slice 8); it replaces the end lines below. Without a result from
  // the server the end lines read as before.
  const runEndRecord = useRunEndRecord(executionId, runEnded, recordedRun)
  const runEnd = useMemo(() => runEndReading(runEndRecord), [runEndRecord])
  // Why the run ended and what to do next; the stored code follows in small text (agion/Agion#2947).
  const runEndLine = runEnd ? runEndLineText(t, runEnd, i18n?.language ?? 'en') : null
  const runEndCode = runEnd ? runEndCodeText(t, runEnd) : null
  const runEndCodeLine = runEndCode ? <span className="block text-xs text-slate-500 dark:text-slate-400" data-testid="mission-run-end-code">{runEndCode}</span> : null
  // What the run shortened at its limits (P-095 slice 11B-4), beside the end lines.
  const cutNote = runCutNote(t, runEndRecord)

  // Latest live messages for the launch handler, which must not re-create on every event.
  const messagesRef = useRef<StreamingChatMessage[]>(messages)
  messagesRef.current = messages
  const carriedRef = useRef<StreamingChatMessage[]>(carriedMessages)
  carriedRef.current = carriedMessages

  // A run this chat launched shows its stored record once it ends, whether
  // or not the live stream delivered it (agion/Agion#2871). The end comes
  // from the stream, or from the run's status read when the stream reconnects
  // or the page is visible again.
  const readLaunchedRun = useCallback(async (runId: string, runHostId: string | undefined) => {
    let recordedHost = runHostId
    if (!recordedHost) {
      try {
        recordedHost = (await executionsApi.getExecution(runId)).edge_host_id || undefined
      } catch (err) {
        console.warn('[StreamingCustomerChat] Failed to read the run host:', err)
      }
    }
    const hostId = resolveEdgeHost({ runHostId: recordedHost, missionHostId: mission?.hostId })
    if (!hostId) return null
    const { messages: stored, channelUserNames } = await readRunHistory(hostId, missionId, runId)
    if (Object.keys(channelUserNames).length > 0) {
      setResolvedChannelUsers((names) => ({ ...names, ...channelUserNames }))
    }
    return stored
  }, [missionId, mission?.hostId])
  const showStoredRun = useCallback((_runId: string, stored: StreamingChatMessage[]) => {
    const rows = storedRunRows({ carried: carriedRef.current, live: messagesRef.current, stored })
    if (rows) setInitialMessages(rows)
  }, [setInitialMessages])
  const markRunEnded = useCallback((_runId: string, status: string) => {
    setExecutionStatus(status)
  }, [])
  useLaunchedRunEnding({
    runId: launchedRun && launchedRun === executionId ? launchedRun : null,
    streamEnded: isCompletedLive || isFailedLive,
    statusEnded: isHistoryOnly,
    isConnected,
    readStoredRun: readLaunchedRun,
    onStatusEnded: markRunEnded,
    onStoredRun: showStoredRun,
  })

  // Notify parent when messages change (used by MissionChatWithArtifacts for artifact extraction)
  useEffect(() => {
    onMessagesChange?.(messages)
  }, [messages, onMessagesChange])

  // The run's state for an Activity sidebar that states it (agion/Agion#2827),
  // read from the same flags the end-of-run lines read.
  // A run the user stopped, as its recorded status or outcome says, reads
  // stopped, as the stopped line under the conversation does (agion/Agion#2947).
  const runStatus: ActivityRunStatus | undefined = runEnded
    ? isStopped
      ? 'stopped'
      : isFailed && !neutralOutcome
        ? 'failed'
        : 'completed'
    : isPaused
      ? 'paused'
      : isRunning
        ? 'running'
        : undefined
  useEffect(() => {
    onRunStatusChange?.(runStatus)
  }, [runStatus, onRunStatusChange])

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  // Track when mission starts producing events or history is loaded
  useEffect(() => {
    if (messages.length > 0 && !isRunning) {
      setIsRunning(true)
    }
  }, [messages.length, isRunning])

  // Sync executionId state with prop — when the user clicks a different execution
  // in the sidebar, the prop changes but useState doesn't re-initialize.
  useEffect(() => {
    setExecutionId(executionIdProp)
    if (executionIdProp) {
      setIsRunning(true)
    }
  }, [executionIdProp])

  // Auto-launch on mount if autostart=true. A mission with a required start
  // value opens its form instead of starting blind (agion/Agion#2413). An
  // autostarted run goes with the mission's defaults, never with a value
  // remembered from an earlier run that nobody saw (agion/Agion#3597).
  useEffect(() => {
    if (autostart && !isRunning && !isLaunching && !executionId && !launchForm.hasRequired) {
      handleLaunchMission(launchForm.inputs.length > 0 ? { typed: initialLaunchValues(launchForm.inputs, null) } : undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autostart])

  // Fetch execution status and load history for past executions.
  // CRITICAL: clear messages immediately on execution switch to prevent
  // showing stale data from the previous execution.
  useEffect(() => {
    // The address now names the run this chat just launched, which the
    // chat already shows (agion/Agion#2327).
    const launched = launchedExecutionRef.current
    if (executionIdProp !== launched) {
      launchedExecutionRef.current = null
      setLaunchedRun(null)
    }
    if (!executionIdProp || executionIdProp === launched) return

    // Immediately clear all state from previous execution
    setExecutionStatus(null)
    setExecutionCreatedAt(null)
    setInitialMessages([])
    setCarriedMessages([])
    setResolvedChannelUsers({})
    setHistoryFailure(null)
    setClosedRun(null)
    setPastRunHostId(undefined)

    let cancelled = false

    const loadHistory = async () => {
      // 1. Fetch execution status, and the host the run was dispatched to
      let runHostId: string | undefined
      try {
        const execution = await executionsApi.getExecution(executionIdProp)
        if (cancelled) return
        runHostId = execution.edge_host_id || undefined
        setPastRunHostId(runHostId)
        setRecordedRun({ executionId: executionIdProp, execution })
        setExecutionStatus(execution.status)
        const createdAt = (execution as any).createdAt || execution.created_at
        if (createdAt) {
          setExecutionCreatedAt(createdAt)
        }
        // The server does not open this run's content to the reader: its
        // conversation is not read (agion/Agion#3019).
        if (execution.content_open === false) {
          setClosedRun({ initiatorType: execution.initiator_type })
          return
        }
      } catch (err) {
        console.warn('[StreamingCustomerChat] Failed to fetch execution status:', err)
        if (!cancelled) {
          setExecutionStatus('completed')
          setHistoryFailure(err)
        }
      }

      // 2. Load the run's history from the Edge host that holds it: the host
      // dispatch recorded for the run, else the mission's own (agion/Agion#2485).
      const hostId = resolveEdgeHost({ runHostId, missionHostId: mission?.hostId })
      if (!hostId) {
        // A group-bound run dispatched before hosts were recorded: its host is
        // unknown, so its history cannot be asked for. Say so rather than show
        // a run that said nothing (agion/Agion#2481).
        if (mission?.hostGroupId && !cancelled) setHistoryFailure(new EdgeHostUnknownError())
        return
      }

      setIsLoadingHistory(true)
      try {
        const { messages: historicalMessages, channelUserNames } = await readRunHistory(hostId, missionId, executionIdProp)
        if (cancelled) return
        // Always set messages — even if empty (clears any race condition leftovers)
        setInitialMessages(historicalMessages)
        if (Object.keys(channelUserNames).length > 0) {
          setResolvedChannelUsers(channelUserNames)
        }
      } catch (err) {
        console.warn('[StreamingCustomerChat] Failed to load chat history:', err)
        if (cancelled) return
        if (isConversationNotViewable(err)) setClosedRun({})
        else setHistoryFailure(err)
      } finally {
        if (!cancelled) setIsLoadingHistory(false)
      }
    }

    loadHistory()
    return () => { cancelled = true }
  }, [executionIdProp, missionId, mission?.hostId, mission?.hostGroupId, setInitialMessages, historyAttempt])

  /**
   * Launch the mission with optional file attachments and initial message.
   * File attachments are formatted in dual format for compatibility:
   * - Flat keys (file_id, filename, content_type): For workflow-mode tool node InputMapping
   * - Array key (attachments): For adaptive-mode formatAttachmentAnnotation()
   * - message key: User's initial message text
   *
   * Flat keys describe the selected file_id, or the first file for legacy launches.
   *
   * Resolves true when the run started. The launch message shows its
   * delivery: sending, then delivered, or failed with the reason while its
   * text and files stay in the composer (phase 4, agion/Agion#2327).
   */
  const handleLaunchMission = useCallback(async (options?: {
    fileAttachments?: UserMessageAttachment[];
    message?: string;
    /** Start values typed outside the form: the bottom field holding the one text start value. */
    typed?: Record<string, string>;
  }): Promise<boolean> => {
    if (!missionId || isLaunchingRef.current || runNotReadyRef.current) return false
    // A required start value still empty stops the run before anything goes
    // out; the form marks it (agion/Agion#2413).
    const fileAttachments = options?.fileAttachments ?? getAttachments()
    const startValues = takeStartValues(options?.typed)
    if (!startValues || pendingFiles.some((file) => file.status !== 'complete')) return false
    isLaunchingRef.current = true
    setIsLaunching(true)
    setLaunchFailure(null)
    const now = Date.now()
    const statusBefore = executionStatus
    const createdAtBefore = executionCreatedAt
    const carriesMessage = !!options?.message?.trim() || fileAttachments.length > 0

    // Reset state from any previous execution so the Activity sidebar restarts
    // fresh and the green dot activates. The conversation itself stays on
    // screen: earlier turns and the launch message are carried over, because
    // the streaming hook clears its list when the new execution subscribes.
    setCarriedMessages((carried) => buildCarriedTranscript({
      missionId,
      carried,
      previous: messagesRef.current,
      followUpText: options?.message,
      attachments: fileAttachments.length ? fileAttachments : undefined,
      dividerText: t('chat:customer.newRunDivider', 'New run started. Earlier messages are shown for reference.'),
      now,
      // The launch request goes out now.
      delivery: { state: 'sending' },
    }))
    setExecutionStatus(null)
    setExecutionCreatedAt(null)
    setInitialMessages([])

    try {
      // The text goes as `message` only where the mission reads it; never
      // beside remembered start values no step reads it with (agion/Agion#3597).
      const input = buildLaunchInput({
        entry: launchEntry,
        startValues,
        message: options?.message,
        previousExecutionId: contextParam,
        attachments: fileAttachments,
      })

      // A launch carrying files runs on exactly the host that holds them. A
      // new run after one in this conversation names it, so the server
      // prefers its host (agion/Agion#2485).
      const followed = contextParam ? null : executionId
      const placement = {
        ...(fileAttachments.length > 0 && edgeHostId ? { hostId: edgeHostId } : {}),
        ...(followed ? { previousExecutionId: followed } : {}),
      }
      setFollowedRunHostId(followed ? runEdgeHostId : undefined)
      const result = await (Object.keys(placement).length > 0
        ? missionsApi.launchMission(missionId, input, placement)
        : missionsApi.launchMission(missionId, input))
      if (result?.execution_id) {
        setExecutionId(result.execution_id)
        // The address names the running run, so a reload returns to it
        // (agion/Agion#2327). It replaces the entry, and drops autostart so
        // the reload does not launch again.
        launchedExecutionRef.current = result.execution_id
        setLaunchedRun(result.execution_id)
        const params = new URLSearchParams(searchParams)
        params.delete('autostart')
        params.set('execution', result.execution_id)
        navigate(`/mission/${missionId}?${params}`, { replace: true })
      }
      setIsRunning(true)
      setCarriedMessages((carried) => settleLaunch(carried, now, { started: true }))
      rememberStartValues()
      resetFiles()
      clearFileValues?.()
      return true
    } catch (err) {
      console.error('[StreamingCustomerChat] Failed to launch mission:', err)
      // A refusal the chat knows by code reads from the code, not the
      // server's sentence (ADR-20260925); any other keeps its reason.
      const refusal = launchRefusal(t, err, user?.role, config.adminUrl)
      const reason = refusal?.reason ?? (err instanceof Error && err.message ? err.message : String(err))
      // No run started: the run shown before stays as it was, so sending
      // again launches again.
      setExecutionStatus(statusBefore)
      setExecutionCreatedAt(createdAtBefore)
      setCarriedMessages((carried) => settleLaunch(carried, now, { started: false, reason }))
      if (!carriesMessage) setLaunchFailure(refusal ?? { reason })
      // A refusal for missing start values marks the ones it names.
      refusedForStartValues(err)
      return false
    } finally {
      isLaunchingRef.current = false
      setIsLaunching(false)
    }
  }, [missionId, contextParam, edgeHostId, executionId, runEdgeHostId, setInitialMessages, t, executionStatus, executionCreatedAt, searchParams, navigate, takeStartValues, rememberStartValues, refusedForStartValues, user?.role, getAttachments, pendingFiles, resetFiles, clearFileValues, launchEntry])

  const handleOpenMission = useCallback((childMissionId: string) => {
    navigate(`/mission/${childMissionId}`)
  }, [navigate])

  const handleFeedback = useCallback((messageId: string, agentId: string | undefined, type: 'positive' | 'negative') => {
    apiClient.post('/v1/feedback', buildFeedbackRequest(messageId, agentId, executionId, type)).catch((err) => {
      console.error('[StreamingCustomerChat] Failed to submit feedback:', err)
    })
  }, [executionId])

  // Voice mission detection — show chat input pre-launch for voice missions
  const isVoiceMission = !!mission?.hasVoiceCapability

  // Staged files stay staged after any failure (phase 4, agion/Agion#2327).
  const hasFailedUpload = pendingFiles.some((f) => f.status === 'error')

  const handleSendMessage = useCallback(async () => {
    // A send pressed while a file uploads waits for it (the waiting delivery
    // state) and goes out when the uploads finish.
    if (isUploading) {
      setSendHeld(true)
      return
    }
    // A file that did not upload stays staged: nothing goes out without it
    // until it is retried or removed.
    if (hasFailedUpload) return
    const attachments = getAttachments()
    // An optional one text start value may be left empty: the run starts
    // without it, and the platform fills its default (agion/Agion#3597).
    const startsRun = !isRunning || isCompleted || isFailed
    const emptyAllowed = startsRun && launchEntry.kind === 'startValue' && !launchEntry.input.required
    if (!inputValue.trim() && attachments.length === 0 && !emptyAllowed) return
    // Auto-launch new execution if mission is not running or previous execution ended
    if (!isRunning || isCompleted || isFailed) {
      // The start values form takes the place of the message field: no text
      // starts a run there (agion/Agion#3597).
      if (launchEntry.kind === 'form') return
      // The launch input delivers the text to the mission and the carried
      // transcript displays it, so it is not also sent as a chat message. On
      // the one text start value the text is that value, even when empty, so
      // a remembered value never goes out unseen.
      const text = inputValue.trim()
      const started = await handleLaunchMission({
        fileAttachments: attachments.length > 0 ? attachments : undefined,
        message: text || undefined,
        ...(launchEntry.kind === 'startValue' ? { typed: { [launchEntry.input.name]: text } } : {}),
      })
      // A launch that failed keeps the text and the files; its message says why.
      if (!started) return
    } else {
      // Nothing was sent (no open free-text question, and a run that takes
      // no message while the agent works): keep the text and files.
      const sent = sendMessage(inputValue.trim(), attachments.length > 0 ? attachments : undefined)
      if (sent === false) return
      resetFiles()
      clearFileValues?.()
    }
    setInputValue('')
  }, [inputValue, sendMessage, getAttachments, resetFiles, isRunning, isCompleted, isFailed, handleLaunchMission, isUploading, hasFailedUpload, clearFileValues, launchEntry])

  // A held send goes out once no file uploads any more. A file that failed
  // stops it; the composer keeps everything and says why.
  useEffect(() => {
    if (!sendHeld || isUploading) return
    setSendHeld(false)
    void handleSendMessage()
  }, [sendHeld, isUploading, handleSendMessage])

  // Voice input handler — launches mission if needed, then connects/toggles
  const handleVoiceInput = useCallback(async () => {
    // Already in a voice call — toggle mute
    if (voiceState === 'in_call') {
      toggleMute()
      return
    }
    // Voice room available — connect
    if (voiceState === 'idle' && voiceRoom) {
      voiceConnect()
      return
    }
    // No active voice session or previous execution ended — launch a new execution
    await handleLaunchMission()
    // voiceRoom will arrive via WebSocket → auto-connect in useEffect
  }, [handleLaunchMission, voiceState, voiceRoom, voiceConnect, toggleMute])

  // Build tool results lookup for pairing with tool calls
  const toolResults = new Map<string, { success: boolean; output?: unknown; error?: string; durationMs: number }>()
  for (const msg of messages) {
    if (msg.type === 'tool_result') {
      const data = msg.data as { toolCallId: string; success: boolean; output?: unknown; error?: string; durationMs: number }
      toolResults.set(data.toolCallId, {
        success: data.success,
        output: data.output,
        error: data.error,
        durationMs: data.durationMs,
      })
    }
  }

  // The conversation shows what the placement table puts there
  // (@agion/mission-stream messagePlacement.ts); agent activity stays in the
  // Activity sidebar.
  const visibleMessages = useMemo(() => {
    const shown = carriedMessages.length > 0 ? [...carriedMessages, ...messages] : messages
    return conversationMessages(heldEarlierMessages.length > 0 ? withoutRepeatedRows([...heldEarlierMessages, ...shown]) : shown)
  }, [messages, carriedMessages, heldEarlierMessages])

  // A surface that draws the run's steps (agion/Agion#2827) places each among
  // the messages by the time it started; this run's messages carry them.
  // Without one the feed is the messages alone.
  const RunStepView = surface.RunStep
  const useRunSteps = (RunStepView && surface.useRunSteps) || useNoRunSteps
  const runSteps = useRunSteps({
    messages,
    live: isRunning && !runEnded && !isPaused,
    agentNameMap: mission?.agentNameMap,
    orchestratorAgentId: mission?.primaryAgentId || mission?.orchestratorId,
  })
  const feed = useMemo(() => placeRunSteps(visibleMessages, runSteps), [visibleMessages, runSteps])

  // The bottom fade paints only while the thread actually scrolls behind a
  // visible composer (agion/Agion#2612 item 4) — isRunning || isVoiceMission
  // is the same condition that renders the composer below. jsdom/SSR-safe:
  // without ResizeObserver the fade just never turns on (shouldShowChatFade
  // is still unit-tested directly).
  const composerVisible = isRunning || isVoiceMission
  useEffect(() => {
    const el = messagesScrollRef.current
    if (!el) return
    const update = () => setHasScrollFade(shouldShowChatFade(el, composerVisible))
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    ro?.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro?.disconnect()
    }
  }, [composerVisible, visibleMessages.length])

  // Compute message grouping — consecutive messages from same sender share one header.
  // Returns { head, tail } senders for each message to detect group boundaries.
  type Sender = string | null
  const getMessageSenders = (msg: StreamingChatMessage): { head: Sender; tail: Sender } => {
    switch (msg.type) {
      case 'agent_turn': {
        const d = msg.data as { userPrompt?: string; assistantResponse?: string }
        const hasUser = !!d.userPrompt
        const hasAgent = !!d.assistantResponse
        const agentKey = `agent:${msg.agentId || ''}`
        return {
          head: hasUser ? 'user' : hasAgent ? agentKey : null,
          tail: hasAgent ? agentKey : hasUser ? 'user' : null,
        }
      }
      case 'hitl_request': {
        const d = msg.data as { modality: string }
        if (d.modality === 'message') {
          const key = `agent:${msg.agentId || ''}`
          return { head: key, tail: key }
        }
        return { head: null, tail: null }
      }
      case 'hitl_response':
        return { head: 'user', tail: 'user' }
      case 'transcript': {
        const d = msg.data as { speaker: 'user' | 'agent' }
        if (d.speaker === 'user') return { head: 'user', tail: 'user' }
        const agentKey = `agent:${msg.agentId || ''}`
        return { head: agentKey, tail: agentKey }
      }
      // Agent-associated types — group with agent responses
      case 'agent_thinking':
      case 'tool_call': {
        const agentKey = `agent:${msg.agentId || ''}`
        return { head: agentKey, tail: agentKey }
      }
      // System-like types — break message groups
      case 'delegation':
      case 'delegation_result':
      case 'orchestrator_decision':
      case 'governance_event':
        return { head: null, tail: null }
      // File cards, cards and notices, and kinds the conversation never shows
      // (placement module) — break message groups too
      case 'tool_result':
      case 'mission_trigger':
      case 'system_notification':
      case 'operator_turn':
      case 'node_status':
      case 'mission_completed':
      case 'mission_failed':
        return { head: null, tail: null }
      default:
        return unknownKind(msg.type, { head: null, tail: null })
    }
  }

  // The feed position of the last message carrying a member's words: what
  // the run sent after it was never answered (F-CHAT-07).
  let lastAnswerIndex = -1
  feed.forEach((item, idx) => {
    if (item.kind !== 'message') return
    const senders = getMessageSenders(item.message)
    if (senders.head === 'user' || senders.tail === 'user') lastAnswerIndex = idx
  })

  // Filter pending HITLs for context bar:
  // - exclude modality: 'message' (tell-only, no response needed)
  // - exclude modality: 'approval' with governance context (EscalationCard handles inline)
  const contextBarHITLs = pendingHITLs.filter((h) => {
    if (h.modality === 'message') return false
    if (h.modality === 'approval' && h.governanceContext?.ruleName) return false
    return true
  })

  // Resolve agent name for context bar
  const resolveAgentName = (agentId: string): string => {
    if (!agentId) return t('common:agent', 'Agent')
    const displayName = mission?.agentNameMap?.[agentId]
    if (displayName) return displayName
    // Fallback for orchestrator if not in agentNameMap
    if (mission?.primaryAgentId && agentId === mission.primaryAgentId) return t('common:orchestrator', 'Orchestrator')
    if (mission?.orchestratorId && agentId === mission.orchestratorId) return t('common:orchestrator', 'Orchestrator')
    return t('common:agent', 'Agent')
  }

  // Scroll to a HITL message in the feed
  const handleScrollToHITL = useCallback((requestId: string) => {
    document.getElementById(`hitl-req-${requestId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  // The next send starts a run (handleSendMessage). On a mission with start
  // values they take the place of the message field (agion/Agion#3597): the
  // one text start value is the bottom field, labelled with its name, and
  // several, or a file, are the form with a run button.
  const nextSendStartsRun = !isRunning || runEnded
  const startValueField = nextSendStartsRun && launchEntry.kind === 'startValue' ? launchEntry.input : null
  const startValueLabel = startValueField ? humaniseName(startValueField.name) : undefined
  const formReplacesComposer = nextSendStartsRun && launchEntry.kind === 'form'
  // The field opens with the mission's declared default, never a value
  // remembered from a run, and only when it opens as the start value: a
  // reply to a running step never starts with it.
  const startValueDefault = startValueField?.defaultValue ?? ''
  useEffect(() => {
    if (startValueDefault) setInputValue((current) => current || startValueDefault)
  }, [startValueDefault])

  // Determine input placeholder
  const getPlaceholder = (): string => {
    if (startValueField) return t('chat:launchInputs.startValuePlaceholder', 'Type a value and press Enter to start a new run.')
    if (isCompleted || isFailed) return t('chat:customer.typeToRestart', 'Type a message to start a new run...')
    // Show HITL question hint when a free-text HITL is selected
    const freeTextHITL = selectedHITL && !selectedHITL.options?.length ? selectedHITL : null
    if (freeTextHITL) {
      const truncated = freeTextHITL.prompt.length > 50
        ? freeTextHITL.prompt.slice(0, 50) + '...'
        : freeTextHITL.prompt
      return t('chat:customer.replyTo', 'Reply to: {{question}}', { question: truncated })
    }
    return t('chat:customer.typeMessage', 'Type a message...')
  }

  // Input stays enabled for completed/failed missions — typing starts a new execution
  const isInputDisabled = false
  // While the run works with no free-text question open, a run that accepts
  // messages takes one for the agent's next turn (phase 6); in any other run
  // send is off and says why, typing stays possible (phase 4, agion/Agion#2327).
  // A held send and a file that did not upload say so the same way.
  // Text sent with no run going starts a new run, which the server said
  // cannot start: the composer says so instead.
  // Files alone never stand in for an empty required start value.
  const startValueEmpty = !!startValueField?.required && !inputValue.trim()
    ? t('chat:launchInputs.enterValue', 'Enter a value to start the run.')
    : undefined
  const sendDisabledReason = ((!isRunning || runEnded) ? runNotReady : undefined) ?? (pendingFiles.length > 0 ? startValueEmpty : undefined) ?? sendDisabledText(
    t,
    { running: isRunning, ended: runEnded, acceptsMessages },
    selectedHITL,
    { held: sendHeld, failed: hasFailedUpload },
    { tooLong: !fitsInMessage(inputValue) },
  )
  // A mission without a host has nowhere to put a file: attach says so. A
  // group-bound one says where its files would go is still being found, or
  // why no server can take them (agion/Agion#2485).
  const attachBlocked = mission
    ? attachBlockedReason({ hostId: edgeHostId, hostGroupBound: !!mission.hostGroupId, placement })
    : undefined
  const pasteRefusalMessage = pasteRefusal ? pasteRefusalText(t, pasteRefusal, attachBlocked) : null
  const attachmentDisabledReason = attachBlocked ? composerText(t, attachBlocked) : undefined
  // PLAN-20260929 slice 13 follow-up: the composer's own Stop control, wired
  // to the same condition and the same stopMission() the header's Stop
  // button already uses (F5: mission.stop -> streaming-service -> mission.cancel).
  // Passed to Composer only under the next surface (see composerStreamingProps
  // below) — the current chat keeps its own header-only Stop affordance, so
  // ChatInput never receives isLoading/onStop and stays visually unchanged.
  const isComposerStreaming = Boolean(executionId && isRunning && !isCompleted && !isFailed && isConnected)
  // A failed read replaces the pre-launch state: the run did happen.
  const historyFailed = historyFailure !== null
  // A run whose content the server does not open to the reader, from the run
  // read, the transcript read or the live stream (agion/Agion#3019).
  const streamContentClosed = errorCode === CONVERSATION_NOT_VIEWABLE
  const contentClosed = closedRun !== null || streamContentClosed
  const isPreLaunch = !isRunning && !isLoadingHistory && visibleMessages.length === 0 && !historyFailed && !contentClosed
  const launchFailedText = composerText(t, 'launchFailed')
  const launchFailureNote = launchFailure ? (
    <p role="alert" data-testid="launch-failure" className="text-sm text-red-700 dark:text-red-400">
      {launchFailedText} {asSentence(launchFailure.reason)}
      {launchFailure.settings && (
        <>
          {' '}
          <a href={launchFailure.settings.href} className="font-medium underline underline-offset-2">
            {launchFailure.settings.label}
          </a>
        </>
      )}
    </p>
  ) : null

  return (
    <div className="flex h-full">
      {/* Main chat area */}
      <div
        className="flex-1 flex flex-col relative min-w-0 overflow-x-hidden"
        onDragEnter={(e) => {
          e.preventDefault()
          if (isRunning && edgeHostId) setIsDragOver(true)
        }}
        onDragOver={(e) => {
          e.preventDefault()
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false)
          }
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
        }}
      >
        {/* Search bar — h-16 aligns with sidebar headers, no border-b for unified
            top band. The next chat surface folds search into its header as an
            icon instead (agion/Agion#2612 item 2) — this column is empty
            there, but the row stays for Pause/Stop and headerActions. The
            current chat keeps the full-width field exactly as before. */}
        <div className="h-16 flex items-center px-4 relative">
          <div className="flex-1" />
          {!usesNextHeader && (
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                ref={searchInputRef}
                type="text"
                value={messageSearch.query}
                onChange={(e) => {
                  messageSearch.setQuery(e.target.value)
                  if (e.target.value.length >= 2) setIsSearchOpen(true)
                }}
                onFocus={() => {
                  if (messageSearch.query.length >= 2) setIsSearchOpen(true)
                }}
                placeholder={t('chat:search.placeholder', 'Search messages...')}
                className="w-96 h-9 pl-9 pr-8"
              />
              {messageSearch.query && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    messageSearch.clearSearch()
                    setIsSearchOpen(false)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 h-auto"
                >
                  <XCircle className="w-3.5 h-3.5 text-slate-400" />
                </Button>
              )}
            </div>
            {isSearchOpen && (
              <SearchResultsView
                results={messageSearch.results}
                total={messageSearch.total}
                isSearching={messageSearch.isSearching}
                error={messageSearch.error}
                query={messageSearch.query}
                hostsQueried={messageSearch.hostsQueried}
                hostsResponded={messageSearch.hostsResponded}
                onClose={() => setIsSearchOpen(false)}
              />
            )}
          </div>
          )}
          {!usesNextHeader && (
          <div className="flex-1 flex justify-end gap-1">
            {/* F5: Stop button — visible while a live execution is running and
                neither completed nor failed. Routes through useStreamingMissionChat.stopMission
                → MissionStatusService.sendMissionStop → mission.stop WebSocket
                frame → streaming-service translates to mission.cancel on the host. */}
            {executionId && isRunning && !isCompleted && !isFailed && isConnected && (
              <>
                {/* Pause/resume — same mission.pause/mission.resume frames the
                    designer sends; the host pauses at its next checkpoint. */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => (isPaused ? resumeMission() : pauseMission())}
                  aria-label={isPaused
                    ? t('chat:customer.resumeMission', 'Resume mission')
                    : t('chat:customer.pauseMission', 'Pause mission')}
                  title={isPaused
                    ? t('chat:customer.resumeMission', 'Resume mission')
                    : t('chat:customer.pauseMission', 'Pause mission')}
                  data-testid="mission-pause-button"
                >
                  {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                  {isPaused ? t('chat:customer.resume', 'Resume') : t('chat:customer.pause', 'Pause')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => stopMission()}
                  aria-label={t('chat:customer.stopMission', 'Stop mission')}
                  title={t('chat:customer.stopMission', 'Stop mission')}
                  data-testid="mission-stop-button"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  {t('chat:customer.stop', 'Stop')}
                </Button>
              </>
            )}
            {headerActions}
          </div>
          )}
        </div>
        {/* Mission header — expandable for context details */}
        <Header
          missionName={mission?.name || mission?.title || t('chat:unknownMission', 'Mission')}
          executionCreatedAt={executionCreatedAt}
          trustTier={trustTier}
          hostName={hostName}
          isConnected={isConnected}
          isHistoryOnly={isHistoryOnly}
          executionId={executionId}
          pastExecutions={pastExecutions}
          onNavigateExecution={(execId) => navigate(`/mission/${missionId}?execution=${execId}`)}
          {...(usesNextHeader ? {
            search: {
              query: messageSearch.query,
              onQueryChange: (q: string) => {
                messageSearch.setQuery(q)
                setIsSearchOpen(q.length >= 2)
              },
              isOpen: isSearchOpen,
              onOpenChange: (open: boolean) => {
                setIsSearchOpen(open)
                if (!open) messageSearch.clearSearch()
              },
              resultsSlot: isSearchOpen ? (
                <SearchResultsView
                  results={messageSearch.results}
                  total={messageSearch.total}
                  isSearching={messageSearch.isSearching}
                  error={messageSearch.error}
                  query={messageSearch.query}
                  hostsQueried={messageSearch.hostsQueried}
                  hostsResponded={messageSearch.hostsResponded}
                  onClose={() => setIsSearchOpen(false)}
                />
              ) : undefined,
            },
            // The top band's run controls and headerActionItems fold into the
            // header's own row instead of a separate icon row above it
            // (agion/Agion issue 2612 item 5). Pause/resume and Stop keep
            // their own current labels as the action's accessible name.
            actions: [
              ...(executionId && isRunning && !isCompleted && !isFailed && isConnected ? [
                {
                  key: 'pause-resume',
                  label: isPaused ? t('chat:customer.resumeMission', 'Resume mission') : t('chat:customer.pauseMission', 'Pause mission'),
                  icon: isPaused ? Play : Pause,
                  onClick: () => (isPaused ? resumeMission() : pauseMission()),
                },
                {
                  key: 'stop',
                  label: t('chat:customer.stopMission', 'Stop mission'),
                  icon: XCircle,
                  onClick: () => stopMission(),
                },
              ] : []),
              ...(headerActionItems ?? []),
            ],
          } : {})}
        />

        {/* Voice error */}
        {voiceError && (
          <div className="px-4 py-2 bg-rose-50 dark:bg-rose-900/20 border-b border-rose-200 dark:border-rose-800 text-xs text-rose-700 dark:text-rose-300">
            {voiceError}
          </div>
        )}

        {/* Drag-and-drop overlay. A drop anywhere on it is taken, and every
            file goes through the upload hook, which refuses one over the
            limit with a reason on its chip (phase 4, agion/Agion#2327). */}
        {isDragOver && isRunning && edgeHostId && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm"
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsDragOver(false)
              const dropped = Array.from(e.dataTransfer?.files ?? [])
              if (dropped.length > 0) addFiles(dropped)
            }}
          >
            <div className="flex w-64 h-48 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-500 bg-blue-50 p-6 text-center text-slate-600 dark:bg-blue-950/20 dark:text-slate-400">
              <svg className="h-10 w-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm font-medium">Drop files to attach</p>
              <p className="text-xs text-slate-400">Max 25 MB per file</p>
            </div>
          </div>
        )}

        {/* Messages area. data-testid is a stable hook only — it changes
            nothing for the current chat; the next scope uses it to centre
            the column at the composer's own max-w-3xl (PLAN-20260929 slice
            13 follow-up: the composer and the turns must share one reading
            column, not the composer narrower than the full-width turns). */}
        <div
          ref={messagesScrollRef}
          className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-4"
          data-testid="chat-messages"
          {...(hasScrollFade ? { 'data-scroll-fade': 'true' } : {})}
        >
          {/* Loading history indicator. A full-height state drops the list's
              gap below it (mb-0!): the scroll anchor follows it, and the gap
              made the empty area overflow and show a scrollbar. */}
          {isLoadingHistory && visibleMessages.length === 0 && (
            <div className="flex items-center justify-center h-full mb-0!">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto mb-2" />
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {t('chat:customer.loadingHistory', 'Loading conversation...')}
                </span>
              </div>
            </div>
          )}

          {historyFailed && (
            <HistoryFailure failure={historyFailure} onRetry={() => setHistoryAttempt((n) => n + 1)} />
          )}

          {contentClosed && <ClosedConversationNotice initiatorType={closedRun?.initiatorType} />}

          {runHostMoved && (
            <div data-testid="run-host-moved">
              <SystemNote variant="warning" isLast>
                {t('chat:customer.runHostMoved', 'This run started on another Edge host because the one that holds the earlier conversation is not available. Files from earlier messages are not available to this run.')}
              </SystemNote>
            </div>
          )}

          {/* Pre-launch state — voice missions show hint to use chat input instead of Run button */}
          {isPreLaunch && (
            <div className="flex items-center justify-center h-full mb-0!">
              <div className="text-center max-w-md px-6">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  {isVoiceMission
                    ? <Phone className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    : <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
                </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                  {mission?.name || t('chat:unknownMission', 'Mission')}
                </h2>
                {mission?.description && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    {mission.description}
                  </p>
                )}
                {/* The one text start value is the bottom field (agion/Agion#3597). */}
                {!startValueField && <LaunchInputs form={launchForm} disabled={isLaunching} className="mb-4" />}
                {startValueField ? null : isVoiceMission ? (
                  isLaunching ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('chat:customer.voiceConnecting', 'Starting voice session...')}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {t('chat:customer.voiceHint', 'Press the microphone button below to start a voice conversation, or type a message.')}
                    </p>
                  )
                ) : (
                  <Button
                    onClick={() => handleLaunchMission()}
                    disabled={isLaunching || !!runNotReady}
                    variant="primary"
                    data-testid="run-mission-button"
                  >
                    {isLaunching
                      ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('pages:missionChat.launching', 'Starting...')}</>
                      : t('pages:missionChat.runMission', 'Run Mission')}
                  </Button>
                )}
                {runNotReady && (
                  <p data-testid="run-not-ready" className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                    {runNotReady}
                  </p>
                )}
                {launchFailureNote && <div className="mt-3">{launchFailureNote}</div>}
              </div>
            </div>
          )}

          {/* Message feed */}
          {feed.map((item, idx) => {
            if (item.kind === 'step') {
              if (!RunStepView) return null
              const { key, startedAt: _startedAt, ...step } = item.step
              return <RunStepView key={`step-${key}`} {...step} executionMode={mission?.executionMode} />
            }
            const msg = item.message
            // A step between two messages ends a group, as a notice does.
            const before = idx > 0 ? feed[idx - 1]! : null
            const prev = before ? (before.kind === 'message' ? getMessageSenders(before.message) : { head: null, tail: null }) : null
            const curr = getMessageSenders(msg)

            // Hide user header if previous message ended with 'user' and this one starts with 'user'
            const hideUserHeader = !!(prev && curr.head === 'user' && prev.tail === 'user')
            // Hide agent header if previous message ended with same agent and this one starts with same agent
            const hideAgentHeader = !!(prev && curr.head && curr.head !== 'user' && prev.tail === curr.head)

            return (
              <CustomerMessageRenderer
                key={msg.id}
                message={msg}
                activeHITL={activeHITL}
                pendingHITLs={pendingHITLs}
                runEnded={runEnded}
                runUnsuccessful={isFailed || isStopped}
                afterLastAnswer={idx > lastAnswerIndex}
                toolResults={toolResults}
                onRespondToHITL={respondToHITL}
                onOpenMission={handleOpenMission}
                onFeedback={handleFeedback}
                agentNameMap={mission?.agentNameMap}
                orchestratorAgentId={mission?.primaryAgentId || mission?.orchestratorId}
                currentUserId={user?.id}
                participantNames={{...mission?.participantNames, ...resolvedChannelUsers, ...(user?.id && user?.name ? { [user.id]: user.name } : {})}}
                hideUserHeader={hideUserHeader}
                hideAgentHeader={hideAgentHeader}
                hostId={edgeHostId}
                heldSubject={heldSubject}
              />
            )
          })}

          {/* Why the run ended and what it lacked, from the server's end record */}
          {runEnd && runEndLine && runEnd.result === 'completed_with_limitations' && (
            <div className="flex items-center justify-center py-3">
              <Alert variant="warning" role="status" className="w-auto" data-testid="mission-run-end-line" data-result={runEnd.result}>
                <span className="text-sm">{runEndLine}</span>
                {runEndCodeLine}
              </Alert>
            </div>
          )}
          {runEnd && runEndLine && runEnd.result !== 'completed_with_limitations' && RunStatusLine && (
            <RunStatusLine kind={runEnd.result === 'cancelled' ? 'stopped' : 'failed'} data-testid="mission-run-end-line">{runEndLine}{runEndCodeLine}</RunStatusLine>
          )}
          {runEnd && runEndLine && runEnd.result !== 'completed_with_limitations' && !RunStatusLine && (
            <div className="flex items-center justify-center gap-2 py-3" data-testid="mission-run-end-line" data-result={runEnd.result}>
              {runEnd.result === 'failed' && <XCircle className="h-5 w-5 text-red-500" />}
              <span className={runEnd.result === 'failed' ? 'text-sm text-red-600 dark:text-red-400' : 'text-sm text-slate-500 dark:text-slate-400'}>
                {runEndLine}
                {runEndCodeLine}
              </span>
            </div>
          )}

          {/* Completion message */}
          {!runEndLine && isCompleted && !isStopped && !isFailed && RunStatusLine && (
            <RunStatusLine kind="completed">{t('chat:customer.missionCompleted', 'Mission completed successfully')}</RunStatusLine>
          )}
          {!runEndLine && isCompleted && !isStopped && !isFailed && !RunStatusLine && (
            <div className="flex items-center justify-center gap-2 py-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                {t('chat:customer.missionCompleted', 'Mission completed successfully')}
              </span>
            </div>
          )}

          {/* Stopped by the user — neutral, no success tick */}
          {!runEndLine && isCompleted && isStopped && RunStatusLine && (
            <RunStatusLine kind="stopped" data-testid="mission-stopped-line">{t('chat:customer.missionStopped', 'Run stopped')}</RunStatusLine>
          )}
          {!runEndLine && isCompleted && isStopped && !RunStatusLine && (
            <div className="flex items-center justify-center gap-2 py-3" data-testid="mission-stopped-line">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {t('chat:customer.missionStopped', 'Run stopped')}
              </span>
            </div>
          )}

          {/* Follow-up mission suggestions — removed: "What's next?" panel
              was noisy at the end of completed missions */}

          {/* Finished with an answer to check — neutral notice with what to do */}
          {!runEndLine && isFailed && neutralOutcome && (
            <div className="flex items-center justify-center py-3">
              <Alert variant="warning" role="status" className="w-auto" data-testid="mission-neutral-ending-line" data-outcome={neutralOutcome}>
                <span className="text-sm">
                  {neutralEndingMessage(t, neutralOutcome)}
                </span>
              </Alert>
            </div>
          )}

          {/* A tool stopped the run with its own reason — say what to fix */}
          {!runEndLine && isFailed && !neutralOutcome && toolReportedError && (
            <div className="flex items-center justify-center py-3">
              <Alert variant="warning" role="status" className="w-auto" data-testid="mission-tool-reported-error-line">
                <span className="text-sm">
                  {t('chat:customer.missionToolReportedError', {
                    reason: toolReportedError,
                    defaultValue: 'The tool could not use this input: {{reason}}. Check the input and try again.',
                  })}
                </span>
              </Alert>
            </div>
          )}

          {/* Failure message */}
          {!runEndLine && isFailed && !neutralOutcome && !toolReportedError && RunStatusLine && (
            <RunStatusLine kind="failed" data-testid="mission-failed-line">
              {failureText}
            </RunStatusLine>
          )}
          {!runEndLine && isFailed && !neutralOutcome && !toolReportedError && !RunStatusLine && (
            <div className="flex items-center justify-center gap-2 py-3" data-testid="mission-failed-line">
              <XCircle className="h-5 w-5 text-red-500" />
              <span className="text-sm text-red-600 dark:text-red-400">
                {failureText}
              </span>
            </div>
          )}

          {/* What the run shortened at its limits, from the server's end record */}
          {cutNote && (
            <div className="flex items-center justify-center py-3">
              <Alert variant="info" role="status" className="w-auto" data-testid="run-cut-note">
                <AlertTitle>{cutNote.title}</AlertTitle>
                {cutNote.lines.map((line) => <AlertDescription key={line}>{line}</AlertDescription>)}
                {cutNote.storage && <AlertDescription>{cutNote.storage}</AlertDescription>}
              </Alert>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error banner */}
        {error && !isFailed && !streamContentClosed && (
          <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-300">
            {errorCode ? t(`errors:stream.${errorCode}`, { defaultValue: error }) : error}
          </div>
        )}

        {/* Chat input — always visible when running. HITL context bar floats above.
            Shown for voice missions even before launch so user can initiate conversation. */}
        {(isRunning || isVoiceMission || (isPreLaunch && !!startValueField)) && (
          <div className="px-4 py-3">
            {/* Hidden file input */}
            {/* eslint-disable-next-line no-restricted-syntax -- hidden file input, not a visible form field */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  addFiles(Array.from(e.target.files))
                  e.target.value = ''
                }
              }}
            />

            {/* Voice call strip — compact bar above input when in a voice call */}
            {(voiceState === 'connecting' || voiceState === 'in_call') && (
              <VoiceControls
                state={voiceState}
                isRemoteSpeaking={isRemoteSpeaking}
                isLocalSpeaking={isLocalSpeaking}
                durationSeconds={voiceDuration}
                isMuted={isMuted}
                onToggleMute={toggleMute}
                onDisconnect={voiceDisconnect}
              />
            )}

            {operatorMode && earlierRunsIncomplete && (
              <p className="max-w-3xl mx-auto mb-2 text-xs text-slate-500 dark:text-slate-400" data-testid="operator-earlier-runs-incomplete">
                {t('chat:operatorTakeover.earlierRunsIncomplete', 'Part of the earlier conversation could not be loaded.')}
              </p>
            )}
            {operatorMode && operatorHoldId ? (
              <OperatorTakeoverPanel
                holdUntil={operatorHold?.hold_until}
                holdState={operatorHoldStates[operatorHoldId]}
                replies={holdReplies}
                onSend={(text) => { sendOperatorMessage(operatorHoldId, text) }}
                onHandBack={handBack}
              />
            ) : (<>
            {holdNotViewers && (
              <p className="max-w-3xl mx-auto mb-2 text-xs text-slate-500 dark:text-slate-400" data-testid="operator-takeover-not-held">
                {t('chat:operatorTakeover.notHeld', 'This conversation is not held by you.')}
              </p>
            )}
            {!isPreLaunch && launchFailureNote && <div className="max-w-3xl mx-auto mb-2">{launchFailureNote}</div>}
            {/* A message after an ended run starts a new one: its start values
                first. The one text start value is the bottom field instead, and
                a form that replaces the message field stays open. */}
            {!isPreLaunch && nextSendStartsRun && !startValueField && (
              <LaunchInputs form={launchForm} disabled={isLaunching} className="max-w-3xl mx-auto mb-2" afterRun={!formReplacesComposer} />
            )}
            {pasteRefusalMessage && (
              <div className="max-w-3xl mx-auto mb-2">
                <p role="alert" data-testid="paste-refused" className="text-sm text-red-700 dark:text-red-400">
                  {pasteRefusalMessage}
                </p>
              </div>
            )}

            {/* HITL context bar — shows pending question the user is responding to */}
            {!runEnded && contextBarHITLs.length > 0 && (
              <ContextBar
                pendingHITLs={contextBarHITLs}
                selectedIndex={Math.min(selectedHITLIndex, contextBarHITLs.length - 1)}
                onSelectIndex={selectHITL}
                onScrollToMessage={handleScrollToHITL}
                resolveAgentName={resolveAgentName}
              />
            )}

            {formReplacesComposer ? (
              <div className="max-w-3xl mx-auto flex flex-col items-end gap-2">
                <Button
                  onClick={() => handleLaunchMission()}
                  disabled={isLaunching || !!runNotReady}
                  variant="primary"
                  data-testid="run-mission-button"
                >
                  {isLaunching
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('pages:missionChat.launching', 'Starting...')}</>
                    : t('pages:missionChat.runMission', 'Run Mission')}
                </Button>
                {runNotReady && (
                  <p data-testid="run-not-ready" className="text-sm text-slate-600 dark:text-slate-300">
                    {runNotReady}
                  </p>
                )}
              </div>
            ) : (<>
            {startValueField && (
              <div className="max-w-3xl mx-auto mb-1 px-1">
                <Label id={startValueLabelId} data-testid="start-value-label">
                  {startValueLabel}{startValueField.required ? ' *' : ''}
                </Label>
              </div>
            )}
            <Composer
              {...(startValueField ? { 'aria-labelledby': startValueLabelId } : {})}
              value={inputValue}
              onChange={setInputValue}
              onSubmit={() => handleSendMessage()}
              placeholder={getPlaceholder()}
              data-testid="mission-chat-input"
              disabled={isLaunching || (isInputDisabled && !isVoiceMission)}
              sendDisabledReason={sendDisabledReason}
              // An optional start value may be sent empty, as a file may be.
              hasAttachments={pendingFiles.length > 0 || (!!startValueField && !startValueField.required)}
              onAttachment={edgeHostId ? () => fileInputRef.current?.click() : undefined}
              attachmentButtonLabel={t('uploadFile', 'Upload file')}
              attachmentDisabledReason={attachmentDisabledReason}
              onPaste={onPaste}
              onVoiceInput={isVoiceMission || voiceRoom ? handleVoiceInput : undefined}
              // Only the next Composer reads isLoading/onStop as Stop-while-
              // streaming; ChatInput also accepts them (disables the field
              // and shows its own stop glyph), so they are withheld from the
              // current chat, which keeps its header-only Stop button.
              {...(surface.Composer ? { isLoading: isComposerStreaming, onStop: () => stopMission() } : {})}
              attachmentSlot={pendingFiles.length > 0 ? (
                <FileAttachment
                  files={pendingFiles.map((f): FileAttachmentFile => ({
                    id: f.id,
                    name: f.filename,
                    size: f.size,
                    type: f.contentType,
                    status: f.status,
                    progress: f.progress,
                    error: f.error,
                  }))}
                  onRemove={removeFile}
                  onRetry={retryFile}
                />
              ) : undefined}
            />
            </>)}
            </>)}
          </div>
        )}
      </div>

      {/* Node progress sidebar removed — Activity sidebar handles execution tracking */}
    </div>
  )
}

export default StreamingCustomerChat
